import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../helpers/withRouter'
import { Page, Spinner } from '../../components'
import { images } from '../../images'
import { AuthenticatedRoutable, IBookingScheduler, IFavoriteResponse, IServiceInfo } from '../../models'
import { urls } from '../../routing'
import { LiteralsService } from '../../services'
import {
  fetchBookingSchedulersAction,
  fetchFavoritesAction,
  fetchMoreFavoritesAction,
  fetchServicesAction,
  setFavoriteNewReservationFieldsAction,
  setSelectedBookingSchedulerModalAction,
  setSelectedFavoriteModalAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'
import { Card } from './Card'
import StyledSection from './styles'
import { handleBottomScroll } from '../../helpers'
import { appsScheduledSelected, appsStyleSelected } from '../../helpers/app'
import { SchedulerCard } from './SchedulerCard'
import { BookingSchedulerModal } from '../../containers'
import { AnyAction } from 'redux'

interface IProps extends DispatchProp<any>, RouteComponentProps {
  isFetchingFavorites: boolean
  isFetchingMoreFavorites: boolean
  isPenalized: boolean
  favorites: IFavoriteResponse[]
  size: number,
  page: number,
  isLoadMoreExist: boolean,
  serviceInfos: IServiceInfo[]
  bookingSchedulers: IBookingScheduler[]
  isFetchingBookingSchedulers: boolean
}

interface IState {
  tab: 'favorites' | 'scheduled'
  // the form modal is open when one of these is set
  schedulingFavorite?: IFavoriteResponse
  editingScheduler?: IBookingScheduler
}

class Favorites extends AuthenticatedRoutable<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = { tab: 'favorites' }
  }

  private get emptyReservations(): ReactNode {
    return (
      <div className="empty-reservations">
        <img src={ images['noFavorites'] } alt={ LiteralsService.get('noReservations') }/>
        <h3>{ LiteralsService.get('noFavorites', true) }</h3>
        <p>{ LiteralsService.get('goToYourListAndMarkFavorites', true) }</p>
      </div>
    )
  }

  private get favoritesContent(): ReactNode {
    const { isFetchingFavorites, isFetchingMoreFavorites, favorites, dispatch } = this.props

    if (isFetchingFavorites) {
      return (
        <article className="spinner-container">
          <Spinner />
        </article>
      )
    } else {
      if (!favorites.length) return this.emptyReservations
      return (
        <>
          {
            favorites.map((favorite, i) => (
              <Card
                key={ i }
                favorite={ favorite }
                onRepeat={ this.repeatFavorite }
                onClick={ () => dispatch(setSelectedFavoriteModalAction(favorite))  }
              />
            ))
          }
          {
            isFetchingMoreFavorites &&
            <article className="spinner-container more">
              <Spinner />
            </article>
          }
        </>
      )
    }
  }

  public componentDidMount() {
    const { dispatch, size, serviceInfos } = this.props
    super.componentDidMount()
    dispatch(fetchFavoritesAction(size, 1))
    window.addEventListener('scroll', this.handleScroll)
    if (serviceInfos.length === 0) dispatch(fetchServicesAction())
    if (appsScheduledSelected?.scheduled) dispatch(fetchBookingSchedulersAction())
  }

  private get tabs(): ReactNode {
    const { tab } = this.state

    return (
      <nav className="tabs">
        <button
          className={ tab === 'favorites' ? 'active' : '' }
          onClick={ () => this.setState({ tab: 'favorites' }) }
        >
          { LiteralsService.get('favorites', true) }
        </button>
        <button
          className={ tab === 'scheduled' ? 'active' : '' }
          onClick={ () => this.setState({ tab: 'scheduled' }) }
        >
          { LiteralsService.get('bookingScheduler', true) }
        </button>
      </nav>
    )
  }

  private get schedulersContent(): ReactNode {
    const { bookingSchedulers, isFetchingBookingSchedulers, favorites, dispatch } = this.props

    if (isFetchingBookingSchedulers) {
      return (
        <article className="spinner-container">
          <Spinner />
        </article>
      )
    }

    if (!favorites.length) return this.emptyReservations

    // A favorite can only carry one scheduler, so the rest are offered for creating one.
    const scheduled = new Set(bookingSchedulers.map(scheduler => scheduler.favorite?.id))

    return (
      <>
        {
          bookingSchedulers.map(scheduler => (
            <SchedulerCard
              key={ `scheduler-${ scheduler.id }` }
              bookingScheduler={ scheduler }
              onEdit={ () => this.setState({ editingScheduler: scheduler }) }
              onDelete={ () => dispatch(setSelectedBookingSchedulerModalAction(scheduler)) }
            />
          ))
        }
        {
          favorites
            .filter(favorite => !scheduled.has(favorite.id))
            .map(favorite => (
              <SchedulerCard
                key={ `no-scheduler-${ favorite.id }` }
                favorite={ favorite }
                onCreate={ () => this.setState({ schedulingFavorite: favorite }) }
              />
            ))
        }
      </>
    )
  }

  private closeSchedulerModal = () => {
    this.setState({ schedulingFavorite: undefined, editingScheduler: undefined })
  }

  componentWillUnmount(): void {
    window.removeEventListener('scroll', this.handleScroll)
  }

  public render(): ReactNode {
    const { tab, schedulingFavorite, editingScheduler } = this.state
    const showTabs = !!appsScheduledSelected?.scheduled

    return (
      <Page className={`authenticated ${appsStyleSelected?.style}`}>
        <StyledSection>
          { showTabs && this.tabs }
          { showTabs && tab === 'scheduled' ? this.schedulersContent : this.favoritesContent }
        </StyledSection>
        {
          (schedulingFavorite || editingScheduler) &&
          <BookingSchedulerModal
            favorite={ schedulingFavorite }
            bookingScheduler={ editingScheduler }
            onClose={ this.closeSchedulerModal }
          />
        }
      </Page>
    )
  }

  private repeatFavorite = (favorite: IFavoriteResponse) => {
    const { dispatch, history } = this.props
    dispatch(setFavoriteNewReservationFieldsAction(favorite))
    history.push(urls.newReservationDateStep)
  }

  private handleScroll = () => {
    const { dispatch, size, page, isLoadMoreExist, isFetchingMoreFavorites } = this.props
    if (isLoadMoreExist && !isFetchingMoreFavorites) {
      handleBottomScroll(() => dispatch(fetchMoreFavoritesAction(size, page))) 
    }
  }
}

const mapStateToProps = ({ favorites, newReservation, bookingScheduler }: IStoreState) => ({
  isFetchingFavorites: favorites.isFetchingFavorites,
  isFetchingMoreFavorites: favorites.isFetchingMoreFavorites,
  favorites: favorites.favorites,
  size: favorites.size,
  page: favorites.page,
  isLoadMoreExist: favorites.isLoadMoreExist,
  serviceInfos: newReservation.serviceInfos,
  bookingSchedulers: bookingScheduler.bookingSchedulers,
  isFetchingBookingSchedulers: bookingScheduler.isFetchingBookingSchedulers
})

export default withRouter(connectRedux(mapStateToProps, Favorites))
